import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ImpressumComponentSkeleton from './ImpressumComponentSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import './ImpressumComponent.scss';

const ImpressumComponent = ({ type = 'impressum' }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    
    const { 
        data: impressumData, 
        loading: dataLoading, 
        error: dataError, 
        refetch 
    } = useCachedFetch('/data/locales/legal.json');

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (dataLoading) return;

        if (dataError) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        const data = impressumData?.pages?.[type];
        
        if (data && data.list && data.list.length > 0) {
            setHasError(false);
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [dataLoading, dataError, impressumData, type]);

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const impressumPageData = useMemo(() => 
        impressumData?.pages?.[type] || null,
        [impressumData, type]
    );

    const getLocalizedText = useCallback((text) => {
        if (!text) return '';
        if (typeof text === 'object') {
            return text[currentLang] || text.de || text.en || '';
        }
        return text;
    }, [currentLang]);

    const sections = useMemo(() => {
        if (!impressumPageData?.list) return [];
        
        return impressumPageData.list.map((section, index) => ({
            ...section,
            localizedTitle: getLocalizedText(section.title),
            localizedContent: section.content.map(block => ({
                ...block,
                localizedValue: getLocalizedText(block.value),
                localizedItems: Array.isArray(getLocalizedText(block.items)) 
                    ? getLocalizedText(block.items) 
                    : []
            }))
        }));
    }, [impressumPageData, getLocalizedText]);

    const renderContentBlock = useCallback((block, index) => {
        if (!block.localizedValue) return null;
        
        switch (block.type) {
            case 'text':
                return (
                    <p className='impressum__rules__item__content' key={index}>
                        {block.localizedValue}
                    </p>
                );
            case 'subtitle':
                return (
                    <h4 className='impressum__rules__item__subtitle' key={index}>
                        {block.localizedValue}
                    </h4>
                );
            case 'list':
                return (
                    <ul className='impressum__rules__item__list' key={index}>
                        {block.localizedItems.map((item, j) => (
                            <li className='impressum__rules__item__list__item' key={j}>
                                {item}
                            </li>
                        ))}
                    </ul>
                );
            default:
                return (
                    <p className='impressum__rules__item__content' key={index}>
                        {block.localizedValue}
                    </p>
                );
        }
    }, []);

    const hasValidData = useMemo(() => 
        !hasError && impressumPageData && sections.length > 0,
        [hasError, impressumPageData, sections.length]
    );

    if (isLoading) {
        return <ImpressumComponentSkeleton />;
    }

    if (!hasValidData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <div className="impressum__wrapper">
            {sections.map((section, index) => (
                <div className='impressum__rules__item' key={index}>
                    <div className='impressum__rules__item__title__wrapper'>
                        <h3 className="impressum__rules__item__title">
                            {section.localizedTitle}
                        </h3>
                    </div>
                    <div className='impressum__rules__item__content__wrapper'>
                        {section.localizedContent.map((block, i) => 
                            renderContentBlock(block, i)
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default React.memo(ImpressumComponent);